/**
 * @file comicApi.js
 * @description 漫画域 API（入参/出参与存量保持一致）
 * @module features/comic/api/comicApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { getAuthToken } from '@/shared/utils/auth'

function comicAuthConfig(token = '') {
  return { headers: { 'COMIC-WEB-TOKEN': token || '' } }
}

function comicJsonAuthConfig(token = '') {
  return {
    headers: {
      'COMIC-WEB-TOKEN': token || '',
      'Content-Type': 'application/json',
    },
  }
}

/**
 * 漫画关键词搜索（已登录时携带 token，便于服务端记录搜索历史）
 * @param {{ keyword: string, pageNum?: number, pageSize?: number }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function searchComicApi(payload) {
  const token = getAuthToken()
  const config = token ? comicAuthConfig(token) : {}
  return axios.post(API.COMIC.SEARCH, payload, config)
}

/**
 * 漫画分类筛选
 * @param {import('../constants/comicClassifyFilters').ClassifyComicPayload} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function classifyComicApi(payload) {
  return axios.post(API.COMIC.CLASSIFY, payload)
}

/**
 * 漫画子主题分类下拉
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchComicThemeSonSelectApi() {
  return axios.get(API.COMIC.THEME_SON_SELECT)
}

/**
 * 漫画详情
 * @param {string} id
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchComicDetailApi(id, token = '') {
  return axios.post(
    API.COMIC.DETAIL,
    { id: String(id).trim() },
    comicAuthConfig(token)
  )
}

/**
 * 漫画阅读目录
 * @param {string|number} comicId
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchComicCatalogApi(comicId, token = '') {
  return axios.post(API.COMIC.CATALOG, { comicId }, comicAuthConfig(token))
}

/**
 * 漫画卷分页图片
 * @param {{ comicId: string|number, volNum: number, pageNum: number, pageSize: number|string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchVolPagesApi(payload, token = '') {
  return axios.post(
    API.COMIC.GET_VOL_PAGES,
    {
      comicId: payload.comicId,
      volNum: payload.volNum,
      pageNum: payload.pageNum,
      pageSize: String(payload.pageSize),
    },
    comicAuthConfig(token)
  )
}

/**
 * 漫画卷互动区数据
 * @param {string|number} volId
 * @param {string} [token]
 * @param {{ pageNum?: number|string, pageSize?: number|string }} [pagination]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchVolInteractApi(volId, token = '', pagination = {}) {
  const payload = { volId: String(volId) }
  if (pagination.pageNum != null && String(pagination.pageNum).trim() !== '') {
    payload.pageNum = String(pagination.pageNum)
  }
  if (pagination.pageSize != null && String(pagination.pageSize).trim() !== '') {
    payload.pageSize = String(pagination.pageSize)
  }
  return axios.post(API.COMIC.VOL_INTERACT, payload, comicAuthConfig(token))
}

/**
 * 同步阅读进度
 * @param {object} progressData
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function syncReadProgressApi(progressData, token = '') {
  return axios.post(API.USER.READ_PROGRESS, progressData, comicAuthConfig(token))
}

/**
 * 弹幕用户配置
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchDanmakuConfApi(token) {
  return axios.post(API.USER_DANMAKU.CONF, {}, comicAuthConfig(token))
}

/**
 * 更新弹幕用户配置
 * @param {object} payload
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function updateDanmakuConfApi(payload, token) {
  return axios.post(API.USER_DANMAKU.CONF_UPDATE, payload, comicJsonAuthConfig(token))
}

/**
 * 弹幕列表查询（未登录可不传 token）
 * @param {{ comicId: string|number, volId: string|number, pageNum?: number }} body
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchDanmakuLaunchQueryApi(body, token = '') {
  const config = token ? comicAuthConfig(token) : {}
  return axios.post(API.USER_DANMAKU.LAUNCH_QUERY, body, config)
}

/**
 * 查询弹幕是否已点赞
 * @param {string|number} danmakuId
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchDanmakuLaunchLikedApi(danmakuId, token) {
  return axios.post(
    API.USER_DANMAKU.LAUNCH_LIKED,
    { danmakuId },
    comicJsonAuthConfig(token)
  )
}

/**
 * 弹幕点赞
 * @param {string|number} danmakuId
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function likeDanmakuLaunchApi(danmakuId, token) {
  return axios.post(
    API.USER_DANMAKU.LAUNCH_LIKE,
    { danmakuId },
    comicJsonAuthConfig(token)
  )
}

/**
 * 弹幕取消点赞
 * @param {string|number} danmakuId
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function unlikeDanmakuLaunchApi(danmakuId, token) {
  return axios.post(
    API.USER_DANMAKU.LAUNCH_UNLIKE,
    { danmakuId },
    comicJsonAuthConfig(token)
  )
}

/**
 * 发送弹幕
 * @param {{ comicId: string|number, volId: string|number, content: string }} payload
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function sendDanmakuLaunchApi(payload, token) {
  return axios.post(API.USER_DANMAKU.LAUNCH_SEND, payload, comicJsonAuthConfig(token))
}
